import { useState } from 'react';
import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  DialogTitle,
  FormControl,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { IContactCompanyLink } from 'src/services/contacts/contacts.types';
import { ContactLinkType } from 'src/services/connections/connections.types';
import { useCopyContactDocumentToCompanyMutation } from 'src/services/connections/connections.service';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  contactDocumentId: string;
  /** The contact's linked companies. The caller only opens this when there is at least one. */
  companyLinks: IContactCompanyLink[];
};

/**
 * Saves a separate copy of a contact's document onto one of the contact's linked companies, so the
 * company keeps it if the contact is deleted. Defaults to the primary company.
 */
export default function ContactDocumentCompanyCopier({ open, onClose, contactDocumentId, companyLinks }: Props) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const defaultLink = companyLinks.find((link) => link.type === ContactLinkType.PRIMARY) ?? companyLinks[0];
  const [companyProfileId, setCompanyProfileId] = useState<string>(defaultLink?.company_profile.id ?? '');

  const [copyToCompany, { isLoading }] = useCopyContactDocumentToCompanyMutation();

  const handleCopy = async () => {
    try {
      await copyToCompany({ profile_id: companyProfileId, contact_document_id: contactDocumentId }).unwrap();
      enqueueSnackbar(t('contacts.documents.copy_to_company_success'), { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(t('contacts.documents.copy_to_company_error'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('contacts.documents.copy_to_company')}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('contacts.documents.copy_to_company_help')}
        </Typography>

        <FormControl fullWidth>
          <InputLabel id="copy-to-company-label">{t('contacts.documents.copy_to_company_label')}</InputLabel>
          <Select
            labelId="copy-to-company-label"
            label={t('contacts.documents.copy_to_company_label')}
            value={companyProfileId}
            onChange={(e) => setCompanyProfileId(e.target.value)}
          >
            {companyLinks.map((link) => (
              <MenuItem key={link.id} value={link.company_profile.id}>
                {link.company_profile.company_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!companyProfileId} onClick={handleCopy}>
          {t('common.copy')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
